import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  PLATFORM_ID,
  ChangeDetectorRef,
} from '@angular/core';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  standalone: true,
  imports: [NgTemplateOutlet]
})
export class SliderComponent<T> implements AfterViewInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input({ required: true }) items!: T[];
  @Input({ required: true }) itemTemplate!: TemplateRef<{ $implicit: T }>;
  @Input() itemGapClass = 'gap-6';
  @Input() sliderClass = '';
  @Input() headerTemplate?: TemplateRef<ElementRef>;
  @Input() titleTemplate?: TemplateRef<ElementRef>;
  @Input() titleText = '';
  @Input() headerTemplateInlineLeft = false;
  @Input() alwaysDisplay = false;

  @Output() isScrollingChange = new EventEmitter<boolean>();

  @ViewChild('slider') slider: ElementRef<HTMLElement>;

  protected isScrolling = false;
  protected isScrollAtStart = true;
  protected isScrollAtEnd = false;
  protected hasOverflow = false;

  private isMouseDown = false;
  private startX = 0;
  private scrollLeft = 0;
  private hasDragged = false;
  protected isBrowser = isPlatformBrowser(this.platformId);
  private resizeObserver?: ResizeObserver;

  private onScroll = (): void => {
    this.updateScrollState();
  };

  protected moveLeft(): void {
    const sliderEl = this.slider?.nativeElement;
    if (!sliderEl) return;

    sliderEl.scrollBy({ left: -sliderEl.clientWidth, behavior: 'smooth' });
  }

  protected moveRight(): void {
    const sliderEl = this.slider?.nativeElement;
    if (!sliderEl) return;

    sliderEl.scrollBy({ left: sliderEl.clientWidth, behavior: 'smooth' });
  }

  ngAfterViewInit(): void {
    this.setupListeners();
  }
  
  private onClick = (e: MouseEvent): void => {
    if (!this.hasDragged) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    this.hasDragged = false;
  };

  private onMouseDown = (e: MouseEvent): void => {
    this.isMouseDown = true;
    this.startX = e.clientX;
    this.scrollLeft = this.slider.nativeElement.scrollLeft;
    this.hasDragged = false;

    e.preventDefault();
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.isMouseDown) return;

    if (!this.isScrolling) {
      this.isScrolling = true;
      this.isScrollingChange.emit(true);
      this.hasDragged = true;
      this.updateScrollState();
    }

    const sliderEl = this.slider?.nativeElement;
    const walk = e.clientX - this.startX;
    sliderEl.scrollLeft = this.scrollLeft - walk;
  };

  private onMouseUp = (e: MouseEvent): void => {
    if (this.isScrolling) {
      this.isScrolling = false;
      this.isScrollingChange.emit(false);
      e.preventDefault();
    }

    this.isMouseDown = false;
  };

  private setupListeners(): void {
    if (!this.isBrowser || !this.slider) return;

    const sliderEl = this.slider.nativeElement;

    sliderEl.addEventListener('click', this.onClick);
    sliderEl.addEventListener('mousedown', this.onMouseDown, { passive: false });
    sliderEl.addEventListener('scroll', this.onScroll, { passive: true });

    this.document.addEventListener('mousemove', this.onMouseMove);
    this.document.addEventListener('mouseup', this.onMouseUp, { passive: false });

    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollState();
    });
    this.resizeObserver.observe(sliderEl);
  }

  private updateScrollState(): void {
    const sliderEl = this.slider?.nativeElement;
    if (!sliderEl) return;

    const { scrollLeft, scrollWidth, clientWidth } = sliderEl;
    const parentVisible = sliderEl.offsetParent !== null;
    
    this.isScrollAtStart = scrollLeft <= 0;
    this.isScrollAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    this.hasOverflow = scrollWidth > clientWidth;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    const sliderEl = this.slider?.nativeElement;

    if (sliderEl) {
      sliderEl.removeEventListener('click', this.onClick);
      sliderEl.removeEventListener('mousedown', this.onMouseDown);
      sliderEl.removeEventListener('scroll', this.onScroll);
    }

    this.document.removeEventListener('mousemove', this.onMouseMove);
    this.document.removeEventListener('mouseup', this.onMouseUp);

    this.resizeObserver?.disconnect();
  }
}